import React from 'react';
import { collect } from 'collect.js';
import HelperMethods from '../../../../services/exports/HelperMethods';
import MathHelpers from '../../../../services/helpers/MathHelpers';
import MetricsContainer from '../../../../components/stats/MetricsContainer';
import Metric from '../../../../components/stats/Metric';
import StatisticsProvider from '../../StatisticsProvider';
import useStatistics from '../../../../hooks/data/useStatistics';

function Metrics() {
  const { stats, loadingMain, getMetricLabel } = useStatistics();

  const percentageFormatter = (value) => HelperMethods.formatPercentage(value);

  const metrics = stats
    ? [
        <Metric
          key={getMetricLabel('delivery_conversion')}
          name={getMetricLabel('delivery_conversion')}
          loading={loadingMain}
          mainMetrics={['delivery_quotes_count', 'deliveries_count', 'delivery_conversion']}
          excludedHistoryMetrics={['delivery_conversion']}
          items={[
            {
              name: getMetricLabel('delivery_quotes_count'),
              ...stats.delivery_quotes_count,
            },
            {
              name: getMetricLabel('deliveries_count'),
              ...stats.deliveries_count,
            },
            {
              name: getMetricLabel('delivery_conversion'),
              formatter: percentageFormatter,
              ...stats.delivery_conversion,
            },
          ]}
        />,
        <Metric
          key={getMetricLabel('delivery_quotes_provider_breakdown')}
          name={getMetricLabel('delivery_quotes_provider_breakdown')}
          loading={loadingMain}
          mainMetrics={['delivery_quotes_provider_breakdown']}
          excludedHistoryMetrics={['*']}
          items={collect(stats.delivery_quotes_provider_breakdown?.value)
            .map((item) => {
              const previous =
                collect(stats.delivery_quotes_provider_breakdown.previous).firstWhere('provider', item.provider)
                  ?.value ?? 0;

              return {
                name: getMetricLabel(item.provider, { defaultValue: item.provider }),
                value: `${item.value} - ${MathHelpers.percentagePart(item.value, stats.delivery_quotes_count.value)}%`,
                previous:
                  previous === 0
                    ? 0
                    : `${previous} - ${MathHelpers.percentagePart(previous, stats.delivery_quotes_count.previous)}%`,
                change: MathHelpers.percentageDiff(item.value, previous),
              };
            })
            .toArray()}
        />,
        <Metric
          key={getMetricLabel('deliveries_provider_breakdown')}
          name={getMetricLabel('deliveries_provider_breakdown')}
          loading={loadingMain}
          mainMetrics={['deliveries_provider_breakdown']}
          excludedHistoryMetrics={['*']}
          items={collect(stats.deliveries_provider_breakdown?.value)
            .map((item) => {
              const previous =
                collect(stats.deliveries_provider_breakdown.previous).firstWhere('provider', item.provider)?.value ?? 0;

              return {
                name: getMetricLabel(item.provider, { defaultValue: item.provider }),
                value: `${item.value} - ${MathHelpers.percentagePart(item.value, stats.deliveries_count.value)}%`,
                previous:
                  previous === 0
                    ? 0
                    : `${previous} - ${MathHelpers.percentagePart(previous, stats.deliveries_count.previous)}%`,
                change: MathHelpers.percentageDiff(item.value, previous),
              };
            })
            .toArray()}
        />,
        <Metric
          key={getMetricLabel('delivery_distance_breakdown')}
          name={getMetricLabel('delivery_distance_breakdown')}
          loading={loadingMain}
          mainMetrics={['delivery_distance_breakdown']}
          excludedHistoryMetrics={['*']}
          items={collect(stats.delivery_distance_breakdown?.value)
            .map((item) => {
              const previous =
                collect(stats.delivery_distance_breakdown.previous).firstWhere('distance', item.distance)?.value ?? 0;

              return {
                name: item.distance,
                value: `${item.value} - ${MathHelpers.percentagePart(item.value, stats.deliveries_count.value)}%`,
                previous:
                  previous === 0
                    ? 0
                    : `${previous} - ${MathHelpers.percentagePart(previous, stats.deliveries_count.previous)}%`,
                change: MathHelpers.percentageDiff(item.value, previous),
              };
            })
            .toArray()}
        />,
      ]
    : [];

  return <MetricsContainer metrics={metrics} />;
}

export default function ExternalDeliveryTab({ dateRangeFilter, renderDateRangeFilter }) {
  return (
    <StatisticsProvider
      dateRangeFilter={dateRangeFilter}
      mainMetrics={[
        'delivery_quotes_count',
        'deliveries_count',
        'delivery_conversion',
        'delivery_quotes_provider_breakdown',
        'deliveries_provider_breakdown',
        'delivery_distance_breakdown',
      ]}
    >
      {renderDateRangeFilter()}
      <Metrics />
    </StatisticsProvider>
  );
}
